import React, { useState, useEffect } from 'react';
import { kpiAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Briefcase, 
  Users, 
  Clock,
  CheckSquare,
  AlertCircle,
  Target,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageLoader } from '../components/common/LoadingSpinner';

export const KPIDashboard = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    try {
      setRefreshing(true);
      const response = await kpiAPI.getDashboard();
      setKpiData(response.data);
    } catch (error) {
      console.error('Failed to fetch KPIs:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatPercent = (value) => {
    const num = parseFloat(value) || 0;
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  const getChangeColor = (value) => {
    const num = parseFloat(value) || 0;
    if (num > 0) return 'text-green-600';
    if (num < 0) return 'text-red-600';
    return 'text-slate-500';
  };

  const getChangeIcon = (value) => {
    const num = parseFloat(value) || 0;
    if (num > 0) return <ArrowUpRight className="h-4 w-4" />;
    if (num < 0) return <ArrowDownRight className="h-4 w-4" />;
    return null;
  };

  if (loading) {
    return <PageLoader text="Loading KPI Dashboard..." />;
  }

  if (!kpiData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Failed to load KPI data</p>
        <Button variant="link" onClick={fetchKPIs}>Try again</Button>
      </div>
    );
  }

  const { revenue, cases, clients, billing, tasks, trends } = kpiData;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="kpi-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">KPI Dashboard</h1>
          <p className="text-slate-500 mt-1">Track your firm's key performance indicators</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchKPIs} 
          disabled={refreshing}
          className="gap-2"
          data-testid="refresh-kpi-btn"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Revenue Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="revenue-cards">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700">Monthly Revenue</p>
                <p className="text-3xl font-bold text-green-900 mt-2">{formatCurrency(revenue.monthly)}</p>
                <div className={`flex items-center gap-1 mt-2 text-sm ${getChangeColor(revenue.monthly_change)}`}>
                  {getChangeIcon(revenue.monthly_change)}
                  <span>{formatPercent(revenue.monthly_change)} vs last month</span>
                </div>
              </div>
              <div className="h-14 w-14 rounded-full bg-green-200 flex items-center justify-center">
                <DollarSign className="h-7 w-7 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Quarterly Revenue</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{formatCurrency(revenue.quarterly)}</p>
              </div>
              <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center">
                <BarChart3 className="h-7 w-7 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Year to Date</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{formatCurrency(revenue.ytd)}</p>
              </div>
              <div className="h-14 w-14 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="h-7 w-7 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-700">Outstanding</p>
                <p className="text-3xl font-bold text-orange-900 mt-2">{formatCurrency(revenue.outstanding)}</p>
              </div>
              <div className="h-14 w-14 rounded-full bg-orange-200 flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-orange-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Metrics Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="cases" data-testid="tab-cases">Cases</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend Chart Placeholder */}
            <Card data-testid="revenue-trend-card">
              <CardHeader>
                <CardTitle className="text-lg">Revenue Trend</CardTitle>
                <CardDescription>Last 6 months performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trends.map((month, idx) => (
                    <div key={month.month} className="flex items-center gap-4">
                      <div className="w-24 text-sm text-slate-500">{month.month_name.split(' ')[0]}</div>
                      <div className="flex-1">
                        <div className="h-8 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                            style={{ 
                              width: `${Math.min(100, (month.revenue / Math.max(...trends.map(t => t.revenue || 1))) * 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                      <div className="w-24 text-right font-medium">{formatCurrency(month.revenue)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-6">
                  <Briefcase className="h-8 w-8 text-blue-600 mb-3" />
                  <p className="text-sm text-slate-500">Active Cases</p>
                  <p className="text-2xl font-bold text-slate-900">{cases.open}</p>
                  <p className="text-xs text-slate-400 mt-1">{cases.new_this_month} new this month</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <Users className="h-8 w-8 text-green-600 mb-3" />
                  <p className="text-sm text-slate-500">Total Clients</p>
                  <p className="text-2xl font-bold text-slate-900">{clients.total}</p>
                  <p className="text-xs text-slate-400 mt-1">{clients.new_this_month} new this month</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-purple-600 mb-3" />
                  <p className="text-sm text-slate-500">Billable Hours</p>
                  <p className="text-2xl font-bold text-slate-900">{billing.monthly_billable_hours}h</p>
                  <p className="text-xs text-slate-400 mt-1">This month</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-orange-600 mb-3" />
                  <p className="text-sm text-slate-500">Collection Rate</p>
                  <p className="text-2xl font-bold text-slate-900">{billing.collection_rate}%</p>
                  <Progress value={billing.collection_rate} className="mt-2" />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Cases Tab */}
        <TabsContent value="cases">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card data-testid="case-metrics-card">
              <CardHeader>
                <CardTitle className="text-lg">Case Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Total Cases</span>
                  <span className="text-2xl font-bold">{cases.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Open Cases</span>
                  <Badge className="bg-blue-100 text-blue-800">{cases.open}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Closed Cases</span>
                  <Badge className="bg-green-100 text-green-800">{cases.closed}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">New This Month</span>
                  <Badge variant="outline">{cases.new_this_month}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Win Rate</CardTitle>
                <CardDescription>Based on resolved cases</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-6">
                  <div className="relative h-32 w-32">
                    <svg className="h-32 w-32 transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#e2e8f0"
                        strokeWidth="12"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="12"
                        strokeDasharray={`${(cases.win_rate / 100) * 352} 352`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold text-slate-900">{cases.win_rate}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">By Practice Area</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(cases.by_practice_area || {}).slice(0, 5).map(([area, count]) => (
                    <div key={area} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{area}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(count / cases.total) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card data-testid="client-metrics-card">
              <CardHeader>
                <CardTitle className="text-lg">Client Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Total Clients</span>
                  <span className="text-2xl font-bold">{clients.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Active Clients</span>
                  <Badge className="bg-green-100 text-green-800">{clients.active}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">New This Month</span>
                  <Badge variant="outline">{clients.new_this_month}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Last Month</span>
                  <Badge variant="secondary">{clients.new_last_month}</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client Growth</CardTitle>
                <CardDescription>Month over month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <p className={`text-4xl font-bold ${getChangeColor(clients.growth_rate)}`}>
                    {formatPercent(clients.growth_rate)}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    {clients.new_this_month} new vs {clients.new_last_month} last month
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Retention Rate</CardTitle>
                <CardDescription>Active in last 90 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-6">
                  <div className="relative h-32 w-32">
                    <svg className="h-32 w-32 transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#e2e8f0"
                        strokeWidth="12"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#8b5cf6"
                        strokeWidth="12"
                        strokeDasharray={`${(clients.retention_rate / 100) * 352} 352`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold text-slate-900">{clients.retention_rate}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card data-testid="billing-metrics-card">
              <CardContent className="p-6">
                <Clock className="h-8 w-8 text-blue-600 mb-3" />
                <p className="text-sm text-slate-500">Total Hours</p>
                <p className="text-2xl font-bold text-slate-900">{billing.total_hours}h</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <Clock className="h-8 w-8 text-green-600 mb-3" />
                <p className="text-sm text-slate-500">Billable Hours</p>
                <p className="text-2xl font-bold text-slate-900">{billing.billable_hours}h</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <Target className="h-8 w-8 text-purple-600 mb-3" />
                <p className="text-sm text-slate-500">Utilization Rate</p>
                <p className="text-2xl font-bold text-slate-900">{billing.utilization_rate}%</p>
                <Progress value={billing.utilization_rate} className="mt-2" />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <DollarSign className="h-8 w-8 text-orange-600 mb-3" />
                <p className="text-sm text-slate-500">Avg Hourly Rate</p>
                <p className="text-2xl font-bold text-slate-900">${billing.average_hourly_rate}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Collection Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600">Collection Rate</span>
                    <span className="text-sm font-medium">{billing.collection_rate}%</span>
                  </div>
                  <Progress value={billing.collection_rate} className="h-3" />
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Collected</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(revenue.total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card data-testid="task-metrics-card">
              <CardContent className="p-6">
                <CheckSquare className="h-8 w-8 text-blue-600 mb-3" />
                <p className="text-sm text-slate-500">Total Tasks</p>
                <p className="text-2xl font-bold text-slate-900">{tasks.total}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <Clock className="h-8 w-8 text-orange-600 mb-3" />
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{tasks.pending}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <CheckSquare className="h-8 w-8 text-green-600 mb-3" />
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{tasks.completed}</p>
              </CardContent>
            </Card>

            <Card className={tasks.overdue > 0 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="p-6">
                <AlertCircle className={`h-8 w-8 ${tasks.overdue > 0 ? 'text-red-600' : 'text-slate-400'} mb-3`} />
                <p className="text-sm text-slate-500">Overdue</p>
                <p className={`text-2xl font-bold ${tasks.overdue > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {tasks.overdue}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Completion Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-6">
                  <div className="relative h-32 w-32">
                    <svg className="h-32 w-32 transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#e2e8f0"
                        strokeWidth="12"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="12"
                        strokeDasharray={`${(tasks.completion_rate / 100) * 352} 352`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold text-slate-900">{tasks.completion_rate}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <p className="text-4xl font-bold text-green-600">{tasks.completed_this_month}</p>
                  <p className="text-sm text-slate-500 mt-2">Tasks completed</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default KPIDashboard;
